import { useMemo } from "react";

import {
  IconButton,
  Link,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { SoftwarePackageVersion } from "@libs/api/gateways/field/field-ops-gateway.dtos";

export interface SoftwarePackageVersionsTableProps
  extends ShimmeredDetailsListProps {}

export const SoftwarePackageVersionsTable = ({
  ...props
}: SoftwarePackageVersionsTableProps) => {
  const theme = useTheme();
  const columns: DataTableColumn<SoftwarePackageVersion>[] = useMemo(() => {
    return [
      {
        key: "version",
        name: "Version",
        minWidth: 100,
        maxWidth: 100,
        isResizable: true,
        sort: true,
        onRender: (softwarePackageVersion: SoftwarePackageVersion) => (
          <Stack
            wrap
            styles={{
              root: { whiteSpace: "initial", width: "100%" }
            }}
            horizontal
            verticalAlign="center"
          >
            <Stack.Item grow>
              <Text>{softwarePackageVersion.version}</Text>
            </Stack.Item>
            <Stack.Item>
              <Link href={softwarePackageVersion.url}>
                <TooltipHost content="Download software package">
                  <IconButton
                    iconProps={{ iconName: "Download" }}
                    disabled={!softwarePackageVersion.url}
                    title={
                      !softwarePackageVersion.url
                        ? "No download available"
                        : undefined
                    }
                  />
                </TooltipHost>
              </Link>
            </Stack.Item>
          </Stack>
        )
      },
      {
        key: "datePublishedUtc",
        name: "Date Published",
        minWidth: 200,
        maxWidth: 200,
        isResizable: true,
        isSorted: true,
        sort: true,
        isSortedDescending: true,
        onRender: (softwarePackageVersion: SoftwarePackageVersion) => {
          return (
            <Stack
              wrap
              styles={{
                root: { whiteSpace: "initial" }
              }}
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: theme.spacing.s1 }}
            >
              <Text>
                {softwarePackageVersion.datePublishedUtc.toDayDefaultFormat()}
              </Text>
              <TextBadge
                badgeSize={TextBadgeSize.small}
                badgeColor={TextBadgeColor.blue}
              >
                {softwarePackageVersion.datePublishedUtc.toRelative()}
              </TextBadge>
            </Stack>
          );
        }
      },

      {
        key: "virusScanIssueCount",
        name: "Virus Scan ",
        minWidth: 80,
        maxWidth: 80,
        isResizable: true,
        sort: true,
        onRender: (softwarePackageVersion: SoftwarePackageVersion) => (
          <Stack
            wrap
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            <Text>{softwarePackageVersion.virusScanIssueCount}</Text>
          </Stack>
        )
      },
      {
        key: "certificateThumbprint",
        name: "Certificate Thumbprint",
        minWidth: 150,
        isResizable: true,
        sort: true,
        onRender: (softwarePackageVersion: SoftwarePackageVersion) => (
          <Stack
            wrap
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            <Text>{softwarePackageVersion.certificateThumbprint}</Text>
          </Stack>
        )
      }
    ];
  }, [theme.spacing.s1]);

  return (
    <DataTable
      stickyHeader
      shimmerLines={10}
      columns={columns}
      onShouldVirtualize={() => true}
      {...props}
    />
  );
};
